import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, degrees } from "pdf-lib";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/FileCard.css";
import hierarchy from "../utils/hierarchy"; // F1: legacy seed fallback only — DB snapshot is the authority
import { useStructureSnapshot } from "../utils/structureSnapshot"; // F1: DB-backed structure (M5 registration-structure)
import { resolveHierarchyDestination } from "../utils/destinationScope";
import { toRecipientPayload } from "../utils/forwardRecipients"; // forwarding payload SSOT (INTERNAL / EXTERNAL / CLERK_NETWORK)
import { getUserFromToken } from "../utils/auth";
import { getApiBaseUrl } from "../utils/baseUrl";
import { fetchFileBlob } from "../utils/secureFileAccess";
import { importBlobToStudio, studioKindForName, studioRouteFor } from "../utils/studioFileImport";
import SignatureCanvas from "react-signature-canvas";
// Wave 3 — FileCard decomposition: presentational + pure helpers extracted.
import {
  AttachmentList,
  DeliveryReceipt,
  FileCardHeader,
  FileHistoryTimeline,
  FileMetaGrid,
  ModalDialog,
  NoticeBanner,
  PasswordPromptModal,
  PdfErrorBoundary,
  PrimaryActions,
  ResubmissionList,
  WorkflowTimeline,
} from "../components/filecard";
import {
  normalizeRouteValue,
  formatTimestamp,
  buildCombinedHistory,
  deriveReceipt,
  derivePerspective,
  getStatusTone,
  isClassified,
  inferStampRankFromRole,
} from "../components/filecard/documentUtils";
import {
  SIG_PEN_COLOR,
  getImageSize,
  recolorSignatureToBlue,
  fileToDataUrl,
  dataUrlToBytes,
  createRoleBoxStampTemplateDataUrl,
} from "../components/filecard/stampImageTools";
import {
  Building2,
  CheckCircle2,
  Clock3,
  Eye,
  FilePenLine,
  MessageSquare,
  Send,
  Stamp,
  Trash2,
  UserRound,
} from "lucide-react";

// ✅ Proper worker setup for react-pdf
const pdfWorkerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// ✅ CMap configuration
const pdfOptions = {
  cMapUrl: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

const API_URL = getApiBaseUrl();
// F1: forwarding options are derived from the DB structure snapshot inside the
// component (structureOptions below) — the static hierarchy module is only the
// offline seed fallback fed into useStructureSnapshot.

const buildRecipientEntries = (tree) => {
  const entries = [];

  Object.entries(tree || {}).forEach(([formation, formationNode]) => {
    const subBranches = formationNode?.subBranches || {};

    Object.entries(subBranches).forEach(([branchName, branchNode]) => {
      if (Array.isArray(branchNode)) {
        branchNode.forEach((recipient) => {
          entries.push({
            formation,
            branch: branchName,
            subBranch: "",
            section: "",
            recipient,
          });
        });
        return;
      }

      if (branchNode && typeof branchNode === "object") {
        Object.entries(branchNode).forEach(([subBranchName, subBranchNode]) => {
          if (Array.isArray(subBranchNode)) {
            subBranchNode.forEach((recipient) => {
              entries.push({
                formation,
                branch: branchName,
                subBranch: subBranchName,
                section: "",
                recipient,
              });
            });
          } else if (subBranchNode && typeof subBranchNode === "object") {
            Object.entries(subBranchNode).forEach(([sectionName, sectionNode]) => {
              if (Array.isArray(sectionNode)) {
                sectionNode.forEach((recipient) => {
                  entries.push({
                    formation,
                    branch: branchName,
                    subBranch: subBranchName,
                    section: sectionName,
                    recipient,
                  });
                });
              }
            });
          }
        });
      }
    });
  });

  return entries;
};


const FileCard = ({
  file,
  onAction,
  role,
  isSummaryView = false,
  isFinalApproved = false,
  token,
  recipientOptions = [],
  restrictToCurrentOffice = false,
  currentOfficeScope = null,
  processing = false,
}) => {
  const navigate = useNavigate();
  const authToken = token || localStorage.getItem("authToken");
  const userFromToken = useMemo(() => getUserFromToken(authToken), [authToken]);

  const [comment, setComment] = useState("");
  const [attachmentsToSend, setAttachmentsToSend] = useState([]);

  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState("");
  const [passwordAction, setPasswordAction] = useState("view");
  const [studioOpening, setStudioOpening] = useState(false);

  const [currentFile, setCurrentFile] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [fileContent, setFileContent] = useState(null);
  const [viewerError, setViewerError] = useState(null);
  const [fileType, setFileType] = useState("");
  const [viewerRevision, setViewerRevision] = useState(null);

  // Signature placement (PDF + images)
  const [signEnabled, setSignEnabled] = useState(false);
  const [sigLoading, setSigLoading] = useState(false);
  const [sigError, setSigError] = useState("");
  const [sigImage, setSigImage] = useState(null); // data URL
  const [useSignature, setUseSignature] = useState(true);
  const [sigPosPx, setSigPosPx] = useState({ x: 50, y: 50 }); // top-left px inside preview
  const [sigPos, setSigPos] = useState({
    xPct: 0.1,
    yPct: 0.1,
    wPct: 0.2,
    hPct: 0.1,
    rotationDeg: 0,
    pageNumber: 1,
    cryptoSign: false,
  }); // normalized for backend
  const [sigDragging, setSigDragging] = useState(null);
  const [sigScale, setSigScale] = useState(1);
  const [sigRotation, setSigRotation] = useState(0);
  const [sigResizeDrag, setSigResizeDrag] = useState(null);

  const [stampLoading, setStampLoading] = useState(false);
  const [stampError, setStampError] = useState("");
  const [stampImage, setStampImage] = useState(null); // data URL
  const [useStamp, setUseStamp] = useState(true);
  const [stampPosPx, setStampPosPx] = useState({ x: 180, y: 50 }); // top-left px inside preview
  const [stampPos, setStampPos] = useState({
    xPct: 0.1,
    yPct: 0.1,
    wPct: 0.2,
    hPct: 0.1,
    rotationDeg: 0,
    pageNumber: 1,
    cryptoSign: false,
  }); // normalized for backend
  const [stampDragging, setStampDragging] = useState(null);
  const [stampScale, setStampScale] = useState(1);
  const [stampRotation, setStampRotation] = useState(0);
  const [stampResizeDrag, setStampResizeDrag] = useState(null);

  const pdfPreviewRef = useRef(null);
  const sigRef = useRef(null);
  const stampRef = useRef(null);
  const sigPadRef = useRef(null);

  const [pdfNumPages, setPdfNumPages] = useState(1);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [showSigPad, setShowSigPad] = useState(false);
  const [showStampUploader, setShowStampUploader] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(16);
  const [savedSignItems, setSavedSignItems] = useState([]);
  const [placementDirty, setPlacementDirty] = useState(false);
  const [savingPlacement, setSavingPlacement] = useState(false);
  const [previewPassword, setPreviewPassword] = useState("");

  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSubBranch, setSelectedSubBranch] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState("");
  // F1: the DB structure snapshot (registration-structure) is the authority for
  // forwarding options and recipient entries; the static hierarchy import is
  // only the offline seed fallback passed into the snapshot hook.
  const structure = useStructureSnapshot(hierarchy);
  const structureOptions = useMemo(
    () => Object.keys(structure).filter((key) => key !== "Others").sort((a, b) => a.localeCompare(b)),
    [structure]
  );
  const [showQuickRecipientSuggestions, setShowQuickRecipientSuggestions] = useState(false);
  const [quickRecipientSearch, setQuickRecipientSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [subBranchFilter, setSubBranchFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [relatedArchiveIndex, setRelatedArchiveIndex] = useState("");
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!restrictToCurrentOffice) return;
    setSelectedRecipient("");
    setQuickRecipientSearch("");
    setShowQuickRecipientSuggestions(false);
  }, [restrictToCurrentOffice, currentOfficeScope?.destinationOfficeId, currentOfficeScope?.routingAssignmentId]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResubmitDialog, setShowResubmitDialog] = useState(false);
  const [resubmitFile, setResubmitFile] = useState(null);
  const [resubmitComment, setResubmitComment] = useState("Corrections completed as requested.");
  const [resubmitBusy, setResubmitBusy] = useState(false);
  const normalizedRole = String(userFromToken?.role || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  const normalizedBranch = String(userFromToken?.branch || localStorage.getItem("branch") || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  const normalizedUnit = String(localStorage.getItem("unit") || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  const showSO2CommsTemplate = ["SO2 COMM", "SO2 COMMS", "SOII COMMS"].includes(normalizedRole);
  const showSO2ITTemplate = ["SO2 IT", "SO II ICT", "SOII ICT"].includes(normalizedRole);
  const showSO1CommsOrITTemplate = ["SO1 COMMS", "SO1 IT"].includes(normalizedRole);
  const showColCisTemplate = normalizedRole === "COL CIS";
  const showHqArmyRoleTemplate =
    Boolean(normalizedRole) &&
    (normalizedUnit === "ARMY_HQS" ||
      normalizedUnit === "ASOB" ||
      normalizedBranch.startsWith("HQ ") ||
      normalizedBranch === "CAU");
  const isArchived = String(file?.status || "").toUpperCase() === "ARCHIVED";

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    // reset when file changes
    setPasswordPrompt("");
    setPasswordError("");
    setShowPasswordPopup(false);
    setCurrentFile(null);

    setComment("");
    setAttachmentsToSend([]);

    // viewer cleanup
    if (fileContent?.startsWith("blob:")) URL.revokeObjectURL(fileContent);
    setShowViewer(false);
    setFileContent(null);
    setViewerError(null);
    setViewerRevision(null);

    // update fileType
    if (file?.fileName) {
      const ext = file.fileName.split(".").pop().toLowerCase();
      setFileType(ext);
    } else {
      setFileType("");
    }

    // signature reset
    setSignEnabled(false);
    setSigError("");
    setStampError("");
    setSigImage(null);
    setStampImage(null);
    setUseSignature(true);
    setUseStamp(true);
    setSigPosPx({ x: 50, y: 50 });
    setSigPos({
      xPct: 0.1,
      yPct: 0.1,
      wPct: 0.2,
      hPct: 0.1,
      rotationDeg: 0,
      pageNumber: 1,
      cryptoSign: false,
    });
    setStampPosPx({ x: 180, y: 50 });
    setStampPos({
      xPct: 0.3,
      yPct: 0.1,
      wPct: 0.2,
      hPct: 0.1,
      rotationDeg: 0,
      pageNumber: 1,
      cryptoSign: false,
    });
    setSigDragging(null);
    setStampDragging(null);
    setPdfNumPages(1);
    setPdfPageNumber(1);
    setSigScale(1);
    setSigRotation(0);
    setStampScale(1);
    setStampRotation(0);
    setShowSigPad(false);
    setShowStampUploader(false);
    setPreviewZoom(1);
    setGridSize(16);
    setSigResizeDrag(null);
    setStampResizeDrag(null);
    setSavedSignItems([]);
    setPlacementDirty(false);
    setSavingPlacement(false);
    setPreviewPassword("");
    setSelectedUnit("");
    setSelectedBranch("");
    setSelectedSubBranch("");
    setSelectedRecipient("");
    setShowQuickRecipientSuggestions(false);
  }, [file?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the current user's stored signature when signing is enabled.
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        setSigError("");
        if (!signEnabled) return;
        if (!authToken) throw new Error("Missing auth token.");
        if (!userFromToken?.userId) throw new Error("Missing userId in token.");

        setSigLoading(true);
        const headers = { Authorization: `Bearer ${authToken}` };
        const res = await fetch(`${API_URL}/api/users/${userFromToken.userId}/signature`, { headers });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(data.message || "Failed to fetch signature.");
        const sigValue =
          typeof data.signature === "string"
            ? data.signature
            : data.signature?.image || null;

        if (!sigValue) {
          setSigImage(null);
          setUseSignature(false);
          setSigError("");
          return;
        }

        const { width, height } = await getImageSize(sigValue, "signature");
        if (width <= 5 || height <= 5) {
          setSigImage(null);
          setUseSignature(false);
          setSigError("Saved signature is too small/blank. Please draw and save a new one.");
          return;
        }

        const normalizedSig = await recolorSignatureToBlue(sigValue).catch(() => sigValue);
        setSigImage(normalizedSig);
        setUseSignature(true);
      } catch (e) {
        setSigImage(null);
        setUseSignature(false);
        setSigError(e.message || "Failed to load signature.");
        setShowSigPad(true);
      } finally {
        setSigLoading(false);
      }
    };
    fetchSignature();
  }, [signEnabled, authToken, userFromToken?.userId]);

  // Fetch the current user's stored stamp when signing is enabled.
  useEffect(() => {
    const fetchStamp = async () => {
      try {
        setStampError("");
        if (!signEnabled) return;
        if (!authToken) throw new Error("Missing auth token.");
        if (!userFromToken?.userId) throw new Error("Missing userId in token.");

        setStampLoading(true);
        const headers = { Authorization: `Bearer ${authToken}` };
        const res = await fetch(`${API_URL}/api/users/${userFromToken.userId}/stamp`, { headers });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(data.message || "Failed to fetch stamp.");
        const stampValue =
          typeof data.stamp === "string"
            ? data.stamp
            : data.stamp?.image || null;

        if (!stampValue) {
          setStampImage(null);
          setUseStamp(false);
          setStampError("");
          return;
        }

        const { width, height } = await getImageSize(stampValue, "stamp");
        if (width <= 5 || height <= 5) {
          setStampImage(null);
          setUseStamp(false);
          setStampError("Saved stamp is too small/blank. Please upload a clear stamp image.");
          return;
        }

        setStampImage(stampValue);
        setUseStamp(true);
      } catch (e) {
        setStampImage(null);
        setUseStamp(false);
        setStampError(e.message || "Failed to load stamp.");
        setShowStampUploader(true);
      } finally {
        setStampLoading(false);
      }
    };
    fetchStamp();
  }, [signEnabled, authToken, userFromToken?.userId]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const snapValue = (v) => {
    if (!snapToGrid || gridSize <= 1) return v;
    return Math.round(v / gridSize) * gridSize;
  };

  const getPreviewSurfaceMetrics = () => {
    const container = pdfPreviewRef.current;
    if (!container) return null;

    const surface =
      container.querySelector(".react-pdf__Page canvas") ||
      container.querySelector(".react-pdf__Page__canvas") ||
      container.querySelector(".file-card__image");

    if (!surface) {
      return {
        container,
        originX: 0,
        originY: 0,
        width: container.clientWidth || 1,
        height: container.clientHeight || 1,
      };
    }

    const containerRect = container.getBoundingClientRect();
    const surfaceRect = surface.getBoundingClientRect();

    return {
      container,
      originX: surfaceRect.left - containerRect.left + container.scrollLeft,
      originY: surfaceRect.top - containerRect.top + container.scrollTop,
      width: surfaceRect.width || 1,
      height: surfaceRect.height || 1,
    };
  };

  const autoScrollPreviewOnDrag = (metrics, pointerEvent) => {
    if (!metrics?.container || !pointerEvent) return;
    const container = metrics.container;
    const rect = container.getBoundingClientRect();
    const edge = 40;
    const step = 22;

    if (pointerEvent.clientY < rect.top + edge) {
      container.scrollTop = Math.max(0, container.scrollTop - step);
    } else if (pointerEvent.clientY > rect.bottom - edge) {
      container.scrollTop = Math.min(
        container.scrollHeight - container.clientHeight,
        container.scrollTop + step
      );
    }

    if (pointerEvent.clientX < rect.left + edge) {
      container.scrollLeft = Math.max(0, container.scrollLeft - step);
    } else if (pointerEvent.clientX > rect.right - edge) {
      container.scrollLeft = Math.min(
        container.scrollWidth - container.clientWidth,
        container.scrollLeft + step
      );
    }
  };

  const updateSigFromPx = (posPx, explicitPageNumber = null) => {
    const metrics = getPreviewSurfaceMetrics();
    const sigEl = sigRef.current;
    if (!metrics || !sigEl) return;
    const { originX, originY, width, height } = metrics;

    const sigW = sigEl.offsetWidth || 100;
    const sigH = sigEl.offsetHeight || 50;

    const xPct = width ? clamp((posPx.x - originX) / width, 0, 1) : 0;
    const yPct = height ? clamp((posPx.y - originY) / height, 0, 1) : 0;
    const wPct = width ? clamp(sigW / width, 0.01, 1) : 0.2;
    const hPct = height ? clamp(sigH / height, 0.01, 1) : 0.1;

    setSigPos((prev) => ({
      ...prev,
      xPct,
      yPct,
      wPct,
      hPct,
      rotationDeg: Number(sigRotation) || 0,
      pageNumber: explicitPageNumber || pdfPageNumber || 1,
    }));
    if (showViewer && signEnabled) setPlacementDirty(true);
  };

  const updateStampFromPx = (posPx, explicitPageNumber = null) => {
    const metrics = getPreviewSurfaceMetrics();
    const stampEl = stampRef.current;
    if (!metrics || !stampEl) return;
    const { originX, originY, width, height } = metrics;

    const stampW = stampEl.offsetWidth || 100;
    const stampH = stampEl.offsetHeight || 50;

    const xPct = width ? clamp((posPx.x - originX) / width, 0, 1) : 0;
    const yPct = height ? clamp((posPx.y - originY) / height, 0, 1) : 0;
    const wPct = width ? clamp(stampW / width, 0.01, 1) : 0.2;
    const hPct = height ? clamp(stampH / height, 0.01, 1) : 0.1;

    setStampPos((prev) => ({
      ...prev,
      xPct,
      yPct,
      wPct,
      hPct,
      rotationDeg: Number(stampRotation) || 0,
      pageNumber: explicitPageNumber || pdfPageNumber || 1,
    }));
    if (showViewer && signEnabled) setPlacementDirty(true);
  };

  // Keep normalized signature metadata in sync when scale/rotation/page changes.
  useEffect(() => {
    if (!signEnabled || !sigImage || !useSignature) return;
    setTimeout(() => updateSigFromPx(sigPosPx, pdfPageNumber), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigScale, sigRotation, pdfPageNumber, previewZoom, sigImage, useSignature]);

  // Keep normalized stamp metadata in sync when scale/rotation/page changes.
  useEffect(() => {
    if (!signEnabled || !stampImage || !useStamp) return;
    setTimeout(() => updateStampFromPx(stampPosPx, pdfPageNumber), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stampScale, stampRotation, pdfPageNumber, previewZoom, stampImage, useStamp]);

  const placeSignatureOnStamp = () => {
    if (!signEnabled || !useSignature || !useStamp || !sigImage || !stampImage) return false;
    const metrics = getPreviewSurfaceMetrics();
    const stampEl = stampRef.current;
    if (!metrics || !stampEl) return false;

    const { originX, originY, width, height } = metrics;
    const stampW = stampEl.offsetWidth || Math.round(120 * stampScale);
    const stampH = stampEl.offsetHeight || Math.round(60 * stampScale);

    // Fit signature to the dotted-line area inside the stamp.
    const nextSigScale = clamp((stampW * 0.55) / 120, 0.3, 3);
    setSigScale(nextSigScale);

    const sigW = Math.round(120 * nextSigScale);
    const sigH = Math.round(60 * nextSigScale);
    const targetX = stampPosPx.x + (stampW - sigW) * 0.25;
    const targetY = stampPosPx.y + stampH * 0.08;

    const minX = originX;
    const minY = originY;
    const maxX = originX + Math.max(0, width - sigW);
    const maxY = originY + Math.max(0, height - sigH);

    const next = {
      x: clamp(targetX, minX, maxX),
      y: clamp(targetY, minY, maxY),
    };

    setSigPosPx(next);
    setTimeout(() => updateSigFromPx(next, pdfPageNumber), 0);
    return true;
  };

  const placeStampNearBottom = () => {
    if (!signEnabled || !useStamp || !stampImage) return false;
    const metrics = getPreviewSurfaceMetrics();
    const stampEl = stampRef.current;
    if (!metrics || !stampEl) return false;

    const { originX, originY, width, height } = metrics;
    const stampW = stampEl.offsetWidth || Math.round(120 * stampScale);
    const stampH = stampEl.offsetHeight || Math.round(60 * stampScale);

    // Place toward the end of page (bottom-right) with small margins.
    const marginX = Math.max(16, Math.round(width * 0.03));
    const marginY = Math.max(16, Math.round(height * 0.05));

    const minX = originX;
    const minY = originY;
    const maxX = originX + Math.max(0, width - stampW);
    const maxY = originY + Math.max(0, height - stampH);

    const next = {
      x: clamp(originX + width - stampW - marginX, minX, maxX),
      y: clamp(originY + height - stampH - marginY, minY, maxY),
    };

    setStampPosPx(next);
    setTimeout(() => updateStampFromPx(next, pdfPageNumber), 0);
    return true;
  };

  // Auto-place signature when stamp and signature are both ready.
  useEffect(() => {
    if (!signEnabled || !useSignature || !useStamp || !sigImage || !stampImage) return;
    const id = setTimeout(() => placeSignatureOnStamp(), 80);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signEnabled, useSignature, useStamp, sigImage, stampImage]);

  useEffect(() => {
    if (!showViewer || !signEnabled) return;
    if ((useSignature && sigImage) || (useStamp && stampImage)) {
      setPlacementDirty(true);
    }
  }, [
    showViewer,
    signEnabled,
    useSignature,
    useStamp,
    sigImage,
    stampImage,
    sigScale,
    stampScale,
    sigRotation,
    stampRotation,
  ]);

  const onSigPointerDown = (e) => {
    if (!signEnabled || !sigImage || !useSignature) return;
    const container = pdfPreviewRef.current;
    const sigEl = sigRef.current;
    if (!container || !sigEl) return;

    e.preventDefault();
    sigEl.setPointerCapture?.(e.pointerId);

    setSigDragging({
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: sigPosPx.x,
      startY: sigPosPx.y,
    });
  };

  const onSigResizePointerDown = (e) => {
    if (!signEnabled || !sigImage || !useSignature) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = e.currentTarget;
    handle.setPointerCapture?.(e.pointerId);

    setSigDragging(null);
    setSigResizeDrag({
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startScale: sigScale,
    });
  };

  const onSigPointerMove = (e) => {
    if (sigResizeDrag && sigResizeDrag.pointerId === e.pointerId) {
      const dx = e.clientX - sigResizeDrag.startClientX;
      const dy = e.clientY - sigResizeDrag.startClientY;
      const delta = Math.max(dx, dy) / 120;
      const nextScale = clamp(sigResizeDrag.startScale + delta, 0.3, 3);
      setSigScale(nextScale);
      setTimeout(() => updateSigFromPx(sigPosPx, pdfPageNumber), 0);
      return;
    }

    if (!sigDragging || sigDragging.pointerId !== e.pointerId) return;
    let metrics = getPreviewSurfaceMetrics();
    const sigEl = sigRef.current;
    if (!metrics || !sigEl) return;
    autoScrollPreviewOnDrag(metrics, e);
    metrics = getPreviewSurfaceMetrics();
    if (!metrics) return;
    const { originX, originY, width, height } = metrics;
    const sigW = sigEl.offsetWidth || 100;
    const sigH = sigEl.offsetHeight || 50;

    const dx = e.clientX - sigDragging.startClientX;
    const dy = e.clientY - sigDragging.startClientY;

    const minX = originX;
    const minY = originY;
    const maxX = originX + Math.max(0, width - sigW);
    const maxY = originY + Math.max(0, height - sigH);
    const rawX = clamp(sigDragging.startX + dx, minX, maxX);
    const rawY = clamp(sigDragging.startY + dy, minY, maxY);

    const nextX = clamp(snapValue(rawX), minX, maxX);
    const nextY = clamp(snapValue(rawY), minY, maxY);

    const next = { x: nextX, y: nextY };
    setSigPosPx(next);
    updateSigFromPx(next);
  };

  const onStampPointerDown = (e) => {
    if (!signEnabled || !stampImage || !useStamp) return;
    const container = pdfPreviewRef.current;
    const stampEl = stampRef.current;
    if (!container || !stampEl) return;

    e.preventDefault();
    stampEl.setPointerCapture?.(e.pointerId);

    setStampDragging({
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: stampPosPx.x,
      startY: stampPosPx.y,
    });
  };

  const onStampResizePointerDown = (e) => {
    if (!signEnabled || !stampImage || !useStamp) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = e.currentTarget;
    handle.setPointerCapture?.(e.pointerId);

    setStampDragging(null);
    setStampResizeDrag({
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startScale: stampScale,
    });
  };

  const onStampPointerMove = (e) => {
    if (stampResizeDrag && stampResizeDrag.pointerId === e.pointerId) {
      const dx = e.clientX - stampResizeDrag.startClientX;
      const dy = e.clientY - stampResizeDrag.startClientY;
      const delta = Math.max(dx, dy) / 120;
      const nextScale = clamp(stampResizeDrag.startScale + delta, 0.3, 3);
      setStampScale(nextScale);
      setTimeout(() => updateStampFromPx(stampPosPx, pdfPageNumber), 0);
      return;
    }

    if (!stampDragging || stampDragging.pointerId !== e.pointerId) return;
    let metrics = getPreviewSurfaceMetrics();
    const stampEl = stampRef.current;
    if (!metrics || !stampEl) return;
    autoScrollPreviewOnDrag(metrics, e);
    metrics = getPreviewSurfaceMetrics();
    if (!metrics) return;
    const { originX, originY, width, height } = metrics;
    const stampW = stampEl.offsetWidth || 100;
    const stampH = stampEl.offsetHeight || 50;

    const dx = e.clientX - stampDragging.startClientX;
    const dy = e.clientY - stampDragging.startClientY;

    const minX = originX;
    const minY = originY;
    const maxX = originX + Math.max(0, width - stampW);
    const maxY = originY + Math.max(0, height - stampH);
    const rawX = clamp(stampDragging.startX + dx, minX, maxX);
    const rawY = clamp(stampDragging.startY + dy, minY, maxY);

    const nextX = clamp(snapValue(rawX), minX, maxX);
    const nextY = clamp(snapValue(rawY), minY, maxY);

    const next = { x: nextX, y: nextY };
    setStampPosPx(next);
    updateStampFromPx(next);
  };

  const onSigPointerUp = (e) => {
    if (sigDragging && sigDragging.pointerId === e.pointerId) {
      setSigDragging(null);
    }
    if (sigResizeDrag && sigResizeDrag.pointerId === e.pointerId) {
      setSigResizeDrag(null);
    }
    if (stampDragging && stampDragging.pointerId === e.pointerId) {
      setStampDragging(null);
    }
    if (stampResizeDrag && stampResizeDrag.pointerId === e.pointerId) {
      setStampResizeDrag(null);
    }
  };

  // ✅ Recipient logic aligned with registration unit model.
  const getRecipients = () => {
    if (restrictToCurrentOffice || (Array.isArray(recipientOptions) && recipientOptions.length > 0)) {
      return Array.from(new Set(recipientOptions)).sort();
    }

    const topLevelNode = hierarchy?.[selectedUnit];
    if (!topLevelNode?.subBranches) return [];

    const branchNode = selectedBranch ? topLevelNode.subBranches?.[selectedBranch] : null;
    const subBranchNode =
      selectedSubBranch && branchNode && typeof branchNode === "object" && !Array.isArray(branchNode)
        ? branchNode[selectedSubBranch]
        : branchNode;
    const targetNode =
      selectedSection && subBranchNode && typeof subBranchNode === "object" && !Array.isArray(subBranchNode)
        ? subBranchNode[selectedSection]
        : subBranchNode;

    if (Array.isArray(targetNode)) {
      return Array.from(new Set(targetNode)).sort();
    }

    if (Array.isArray(subBranchNode)) {
      return Array.from(new Set(subBranchNode)).sort();
    }

    return [];
  };

  const branchOptions = useMemo(() => {
    return Object.keys(hierarchy?.[selectedUnit]?.subBranches || {});
  }, [selectedUnit]);

  const subBranchOptions = useMemo(() => {
    const branchNode = hierarchy?.[selectedUnit]?.subBranches?.[selectedBranch];
    if (!branchNode || Array.isArray(branchNode) || typeof branchNode !== "object") return [];
    return Object.keys(branchNode);
  }, [selectedUnit, selectedBranch]);

  const sectionOptions = useMemo(() => {
    const subBranchNode = hierarchy?.[selectedUnit]?.subBranches?.[selectedBranch]?.[selectedSubBranch];
    if (!subBranchNode || Array.isArray(subBranchNode) || typeof subBranchNode !== "object") return [];
    return Object.keys(subBranchNode);
  }, [selectedUnit, selectedBranch, selectedSubBranch]);

  const filterOptions = (options, query) => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) =>
      String(option || "").toLowerCase().includes(normalizedQuery)
    );
  };

  const findExactOption = (options, value) => {
    const normalizedValue = String(value || "").trim().toLowerCase();
    if (!normalizedValue) return "";
    return options.find(
      (option) => String(option || "").trim().toLowerCase() === normalizedValue
    ) || "";
  };

  const filteredHierarchyOptions = useMemo(
    () => filterOptions(structureOptions, unitFilter),
    [unitFilter, structureOptions]
  );

  const filteredBranchOptions = useMemo(
    () => filterOptions(branchOptions, branchFilter),
    [branchOptions, branchFilter]
  );

  const filteredSubBranchOptions = useMemo(
    () => filterOptions(subBranchOptions, subBranchFilter),
    [subBranchOptions, subBranchFilter]
  );

  const filteredRecipientOptions = useMemo(
    () => filterOptions(getRecipients(), recipientFilter),
    [recipientFilter, selectedUnit, selectedBranch, selectedSubBranch, selectedSection, recipientOptions, restrictToCurrentOffice]
  );

  const allRecipientEntries = useMemo(() => {
    if (restrictToCurrentOffice) return recipientOptions.map((recipient) => ({
      formation: currentOfficeScope?.unit || "", branch: currentOfficeScope?.branch || "",
      subBranch: currentOfficeScope?.subBranch || "", section: "", recipient,
    }));
    if (restrictToCurrentOffice || (Array.isArray(recipientOptions) && recipientOptions.length > 0)) {
      const allowedRecipients = new Set(
        recipientOptions.map((recipient) => normalizeRouteValue(recipient))
      );
      const scopedEntries = buildRecipientEntries(structure).filter((entry) =>
        allowedRecipients.has(normalizeRouteValue(entry.recipient))
      );
      const representedRecipients = new Set(
        scopedEntries.map((entry) => normalizeRouteValue(entry.recipient))
      );
      const unscopedEntries = Array.from(new Set(recipientOptions))
        .filter((recipient) => !representedRecipients.has(normalizeRouteValue(recipient)))
        .map((recipient) => ({
          formation: "",
          branch: "",
          subBranch: "",
          section: "",
          recipient,
        }));
      return [...scopedEntries, ...unscopedEntries];
    }

    return buildRecipientEntries(structure);
  }, [recipientOptions, structure, restrictToCurrentOffice, currentOfficeScope]);

  const filteredQuickRecipientEntries = useMemo(() => {
    const normalizedQuery = String(quickRecipientSearch || "").trim().toLowerCase();
    if (!normalizedQuery) return allRecipientEntries.slice(0, 50);

    return allRecipientEntries.filter(({ recipient, formation, branch, subBranch, section }) =>
      [recipient, formation, branch, subBranch, section]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery))
    );
  }, [allRecipientEntries, quickRecipientSearch]);

  const applyRecipientEntry = (entry) => {
    if (!entry) return;

    setSelectedRecipient(entry.recipient || "");
    setRecipientFilter(entry.recipient || "");
    setQuickRecipientSearch(entry.recipient || "");

    if (entry.formation) {
      setSelectedUnit(entry.formation);
      setUnitFilter(entry.formation);
    }

    if (entry.branch) {
      setSelectedBranch(entry.branch);
      setBranchFilter(entry.branch);
    } else {
      setSelectedBranch("");
      setBranchFilter("");
    }

    if (entry.subBranch) {
      setSelectedSubBranch(entry.subBranch);
      setSubBranchFilter(entry.subBranch);
    } else {
      setSelectedSubBranch("");
      setSubBranchFilter("");
    }

    if (entry.section) {
      setSelectedSection(entry.section);
      setSectionFilter(entry.section);
    } else {
      setSelectedSection("");
      setSectionFilter("");
    }
  };

  const quickRecipientSuggestions = useMemo(() => {
    return filteredQuickRecipientEntries.slice(0, 50).map((entry) => ({
      ...entry,
      label: [entry.recipient, entry.section, entry.subBranch, entry.branch, entry.formation]
        .filter(Boolean)
        .join(" - "),
    }));
  }, [filteredQuickRecipientEntries]);

  const renderQuickRecipientSuggestions = () => {
    if (!showQuickRecipientSuggestions) return null;
    return (
      <div
        role="listbox"
        aria-label="Recipient suggestions"
        style={{
          maxHeight: 240,
          overflowY: "auto",
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          background: "#fff",
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.16)",
          position: "relative",
          zIndex: 20,
          marginTop: -6,
          marginBottom: 10,
        }}
      >
        {quickRecipientSuggestions.length > 0 ? quickRecipientSuggestions.map((entry, index) => (
          <button
            type="button"
            role="option"
            aria-selected={normalizeRouteValue(selectedRecipient) === normalizeRouteValue(entry.recipient)}
            key={`${entry.label}-${index}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              applyRecipientEntry(entry);
              setShowQuickRecipientSuggestions(false);
            }}
            style={{
              display: "block",
              width: "100%",
              padding: "9px 12px",
              textAlign: "left",
              border: 0,
              borderBottom: "1px solid #e2e8f0",
              background: "#fff",
              color: "#0f172a",
              cursor: "pointer",
            }}
          >
            <strong>{entry.recipient}</strong>
            {[entry.section, entry.subBranch, entry.branch, entry.formation].filter(Boolean).length > 0 && (
              <small style={{ display: "block", color: "#64748b", marginTop: 2 }}>
                {[entry.section, entry.subBranch, entry.branch, entry.formation].filter(Boolean).join(" · ")}
              </small>
            )}
          </button>
        )) : (
          <div style={{ padding: "10px 12px", color: "#64748b" }}>
            No matching registered recipient appointment.
          </div>
        )}
      </div>
    );
  };

  const findQuickRecipientEntry = (value) => {
    const wanted = normalizeRouteValue(value);
    if (!wanted) return null;

    const exactLabelMatch = quickRecipientSuggestions.find(
      (entry) => normalizeRouteValue(entry.label) === wanted
    );
    if (exactLabelMatch) return exactLabelMatch;

    const roleMatches = allRecipientEntries.filter(
      (entry) => normalizeRouteValue(entry.recipient) === wanted
    );
    if (roleMatches.length <= 1) return roleMatches[0] || null;

    const currentScope = {
      unit: normalizeRouteValue(file?.unit || userFromToken?.unit || "KA"),
      unitBranch: normalizeRouteValue(file?.unitBranch),
      branch: normalizeRouteValue(file?.branch),
      subBranch: normalizeRouteValue(file?.subBranch),
    };

    return roleMatches.find((entry) => {
      const scope = resolveHierarchyDestination({
        unit: file?.unit || userFromToken?.unit || "KA",
        formation: entry.formation || "",
        branch: entry.branch || "",
        subBranch: entry.subBranch || "",
        section: entry.section || "",
      });

      return (
        normalizeRouteValue(scope.unit) === currentScope.unit &&
        normalizeRouteValue(scope.branch) === currentScope.branch &&
        normalizeRouteValue(scope.subBranch) === currentScope.subBranch &&
        (!currentScope.unitBranch ||
          !normalizeRouteValue(scope.unitBranch) ||
          normalizeRouteValue(scope.unitBranch) === currentScope.unitBranch)
      );
    }) || roleMatches[0];
  };

  const selectedRecipientDetails = useMemo(() => {
    if (!selectedRecipient) return null;

    const roleMatches = allRecipientEntries.filter(
      (entry) => String(entry.recipient || "").trim().toLowerCase() ===
        String(selectedRecipient).trim().toLowerCase()
    );
    const matchedEntry = roleMatches.find((entry) =>
      (!selectedUnit || entry.formation === selectedUnit) &&
      (!selectedBranch || entry.branch === selectedBranch) &&
      (!selectedSubBranch || entry.subBranch === selectedSubBranch) &&
      (!selectedSection || entry.section === selectedSection)
    ) || roleMatches[0];

    return {
      recipient: selectedRecipient,
      formation: matchedEntry?.formation || selectedUnit,
      branch: matchedEntry?.branch || selectedBranch,
      subBranch: matchedEntry?.subBranch || selectedSubBranch,
      section: matchedEntry?.section || selectedSection,
    };
  }, [
    allRecipientEntries,
    selectedBranch,
    selectedRecipient,
    selectedSection,
    selectedSubBranch,
    selectedUnit,
  ]);
  const selectedDestinationScope = useMemo(() => {
    const selectedRole = normalizeRouteValue(selectedRecipientDetails?.recipient);
    const isApprovedForCurrentScope = (recipientOptions || []).some(
      (recipient) => normalizeRouteValue(recipient) === selectedRole
    );

    // recipientOptions is fetched from the backend for this document's stored
    // scope. Preserve that authoritative scope instead of re-deriving it from
    // a display tree that may contain an extra wrapper (for example HQ KA >
    // OPS BRANCH > CIS SUB BRANCH). The wrapper previously changed HQ KA into
    // OPS BRANCH and caused valid recipients such as COL CIS to be rejected.
    if (restrictToCurrentOffice) return currentOfficeScope || {};
    if (selectedRole && isApprovedForCurrentScope) {
      return {
        unit: file?.unit || userFromToken?.unit || "KA",
        unitBranch: file?.unitBranch || "",
        branch: file?.branch || userFromToken?.branch || "",
        subBranch: file?.subBranch || userFromToken?.subBranch || "",
      };
    }

    return resolveHierarchyDestination({
      unit: file?.unit || userFromToken?.unit || "KA",
      formation: selectedRecipientDetails?.formation || "",
      branch: selectedRecipientDetails?.branch || "",
      subBranch: selectedRecipientDetails?.subBranch || "",
      section: selectedRecipientDetails?.section || "",
    });
  }, [
    file?.branch,
    file?.subBranch,
    file?.unit,
    file?.unitBranch,
    recipientOptions,
    restrictToCurrentOffice,
    currentOfficeScope,
    selectedRecipientDetails,
    userFromToken?.branch,
    userFromToken?.subBranch,
    userFromToken?.unit,
  ]);

  // Normalize recipient identity separately from the hierarchy destination above.
  // The backend re-validates every forwarding destination on submission.
  const forwardingPayload = useMemo(
    () => toRecipientPayload(selectedRecipientDetails || { recipient: selectedRecipient }),
    [selectedRecipient, selectedRecipientDetails]
  );

  // ✅ Helpers
  const openBlob = (blob, filename = "file") => {
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    // revoke later
    setTimeout(() => URL.revokeObjectURL(url), 60_000);

    if (!win) {
      // popup blocked → download
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  };

  const fetchMainFileBlob = async ({ fileId, password }) => {
    // Secure flow: password is verified via POST /access (never in the URL);
    // the short-lived accessToken it returns authorizes the download.
    return fetchFileBlob({ fileId, password, token: authToken });
  };

  // -------------------- View Main File --------------------
  const handleViewFile = async (f) => {
    if (!authToken) return setNotice({ type: "error", message: "You must be logged in to view files." });
    if (!f?._id) return setNotice({ type: "error", message: "File ID is missing." });

    if (isClassified(f?.classification)) {
      setCurrentFile(f);
      setPasswordAction("view");
      setShowPasswordPopup(true);
      setPasswordPrompt("");
      setPasswordError("");
      return;
    }

    try {
      const blob = await fetchMainFileBlob({ fileId: f._id });
      openBlob(blob, f.fileName || "file");
    } catch (err) {
      console.error("🚨 View file error:", err);
      setNotice({ type: "error", message: err.message });
    }
  };

  const importFileIntoStudio = async (f, blob) => {
    setStudioOpening(true);
    try {
      const imported = await importBlobToStudio({ blob, fileName: f.originalName || f.fileName, documentType: f.documentType || "", classification: f.classification || "Restricted" });
      navigate(studioRouteFor(imported.kind, imported.id));
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Could not open this file in Document Studio." });
    } finally {
      setStudioOpening(false);
    }
  };

  const handleOpenInStudio = async (f) => {
    if (f?.source === "studio" && f?._id) {
      navigate(studioRouteFor(f.studio?.kind || studioKindForName(f.fileName), f._id));
      return;
    }
    if (!studioKindForName(f?.originalName || f?.fileName)) {
      setNotice({ type: "error", message: "This file type is not supported by Document Studio." });
      return;
    }
    if (isClassified(f?.classification)) {
      setCurrentFile(f);
      setPasswordAction("studio");
      setShowPasswordPopup(true);
      setPasswordPrompt("");
      setPasswordError("");
      return;
    }
    setStudioOpening(true);
    try {
      const blob = await fetchMainFileBlob({ fileId: f._id });
      await importFileIntoStudio(f, blob);
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Could not open this file in Document Studio." });
      setStudioOpening(false);
    }
  };

  // -------------------- Password verify for classified --------------------
  const verifyPassword = async () => {
    setIsVerifyingPassword(true);
    setPasswordError("");

    try {
      if (!authToken) throw new Error("No auth token found.");
      const fileId = currentFile?._id;
      const password = passwordPrompt.trim();
      if (!fileId || !password) throw new Error("Missing file or password.");

      const blob = await fetchMainFileBlob({ fileId, password });

      if (passwordAction === "studio") {
        setShowPasswordPopup(false);
        setPasswordPrompt("");
        await importFileIntoStudio(currentFile, blob);
        return;
      }

      const url = URL.createObjectURL(blob);
      const ext = (currentFile.fileName || "").split(".").pop().toLowerCase();

      setFileType(ext);
      setFileContent(url);
      setShowViewer(true);
      setShowPasswordPopup(false);
      setPreviewPassword(password);
      setPasswordPrompt("");
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const drawOverlayImage = async (ctx, item, targetWidth, targetHeight) => {
    const src = item?.image;
    if (!src || !item?.position) return;
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load overlay image."));
      img.src = src;
    });

    const pos = item.position || {};
    const x = Math.max(0, (Number(pos.xPct) || 0) * targetWidth);
    const y = Math.max(0, (Number(pos.yPct) || 0) * targetHeight);
    const w = Math.max(1, (Number(pos.wPct) || 0.2) * targetWidth);
    const h = Math.max(1, (Number(pos.hPct) || 0.1) * targetHeight);
    const rot = Number(pos.rotationDeg) || 0;

    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  };

  const buildSignedBlob = async (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const sourceBlob = viewerRevision?.version
      ? await fetchRevisionBlob(viewerRevision.version)
      : await fetchMainFileBlob({
          fileId: file?._id,
          password: previewPassword || undefined,
        });

    const sourceName = viewerRevision?.originalName || file?.fileName || "";
    const ext = sourceName.split(".").pop()?.toLowerCase() || "";
    const isImage = ["jpg", "jpeg", "png"].includes(ext);
    const isPdf = ext === "pdf";

    if (isImage) {
      const srcUrl = URL.createObjectURL(sourceBlob);
      try {
        const baseImg = new Image();
        await new Promise((resolve, reject) => {
          baseImg.onload = () => resolve();
          baseImg.onerror = () => reject(new Error("Failed to load main image."));
          baseImg.src = srcUrl;
        });
        const canvas = document.createElement("canvas");
        const width = baseImg.naturalWidth || baseImg.width || 1;
        const height = baseImg.naturalHeight || baseImg.height || 1;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas context unavailable.");
        ctx.drawImage(baseImg, 0, 0, width, height);
        for (const item of items) {
          await drawOverlayImage(ctx, item, width, height);
        }
        const outType = ext === "png" ? "image/png" : "image/jpeg";
        const outBlob = await new Promise((resolve) =>
          canvas.toBlob((b) => resolve(b), outType, 0.95)
        );
        if (!outBlob) throw new Error("Failed to create signed image.");
        return outBlob;
      } finally {
        URL.revokeObjectURL(srcUrl);
      }
    }

    if (isPdf) {
      const srcBytes = await sourceBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(srcBytes);
      const pages = pdfDoc.getPages();
      for (const item of items) {
        if (!item?.image || !item?.position) continue;
        const pos = item.position;
        const pageNumber = Math.max(1, Number(pos.pageNumber) || 1);
        const pageIndex = Math.min(pages.length - 1, pageNumber - 1);
        const page = pages[pageIndex];
        if (!page) continue;

        const bytes = await dataUrlToBytes(item.image);
        const isPng = String(item.image).startsWith("data:image/png");
        const embedded = isPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
        const { width, height } = page.getSize();
        const w = Math.max(1, (Number(pos.wPct) || 0.2) * width);
        const h = Math.max(1, (Number(pos.hPct) || 0.1) * height);
        const x = Math.max(0, (Number(pos.xPct) || 0) * width);
        const yFromTop = Math.max(0, (Number(pos.yPct) || 0) * height);
        const y = Math.max(0, height - yFromTop - h);
        const rot = Number(pos.rotationDeg) || 0;
        const radians = (rot * Math.PI) / 180;
        const centerX = x + w / 2;
        const centerY = y + h / 2;
        const rotatedOriginX = centerX - (w * Math.cos(radians) - h * Math.sin(radians)) / 2;
        const rotatedOriginY = centerY - (w * Math.sin(radians) + h * Math.cos(radians)) / 2;

        page.drawImage(embedded, {
          x: rotatedOriginX,
          y: rotatedOriginY,
          width: w,
          height: h,
          rotate: degrees(rot),
        });
      }
      const outBytes = await pdfDoc.save();
      return new Blob([outBytes], { type: "application/pdf" });
    }

    return null;
  };

  const saveSignedVersionToServer = async (blob, items) => {
    if (!authToken) throw new Error("Missing auth token.");
    if (!file?._id) throw new Error("Missing file id.");
    if (!blob) return;

    const sourceName = viewerRevision?.originalName || file?.fileName || "";
    const ext = sourceName.split(".").pop()?.toLowerCase() || "bin";
    const outName = `signed-${Date.now()}.${ext}`;
    const formData = new FormData();
    formData.append("file", blob, outName);
    formData.append("signItems", JSON.stringify(items || []));
    if (viewerRevision?.version) {
      formData.append("revisionVersion", String(viewerRevision.version));
    }

    const res = await fetch(`${API_URL}/api/uploads/files/${file._id}/signed-version`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || "Failed to save signed version.");
    }
  };

  const dismissViewer = () => {
    if (fileContent?.startsWith("blob:")) URL.revokeObjectURL(fileContent);
    setShowViewer(false);
    setFileContent(null);
    setViewerError(null);
    setViewerRevision(null);
    setPreviewPassword("");
    setPlacementDirty(false);
  };

  const applyAndCloseViewer = async () => {
    if (savingPlacement) return;
    try {
      if (!signEnabled) throw new Error("Enable signing before applying a stamp or signature.");
      const items = buildAllSignItemsForSubmit();
      if (!items.length) throw new Error("Select a stamp or signature to apply.");
      setSavingPlacement(true);
      const signedBlob = await buildSignedBlob(items);
      if (!signedBlob) throw new Error("This document format cannot be stamped in the preview.");
      await saveSignedVersionToServer(signedBlob, items);
      setSavedSignItems(items);
      setPlacementDirty(false);
      setNotice({
        type: "success",
        message: viewerRevision?.version
          ? `Signature/stamp saved on corrected version ${viewerRevision.version}.`
          : "Stamp placement applied and saved.",
      });
      dismissViewer();
    } catch (err) {
      setNotice({ type: "error", message: err.message || "Failed to save signed file." });
    } finally {
      setSavingPlacement(false);
    }
  };

  const openPreviewModal = async (f) => {
    try {
      if (!authToken) throw new Error("🚫 You must be logged in to preview files.");
      if (!f?._id) throw new Error("❗ File ID is missing.");

      if (isClassified(f.classification)) {
        // Reuse password flow, but show preview modal after verification.
        setCurrentFile(f);
        setShowPasswordPopup(true);
        setPasswordPrompt("");
        setPasswordError("");
        return;
      }

      const blob = await fetchMainFileBlob({ fileId: f._id });
      const url = URL.createObjectURL(blob);
      const ext = (f.fileName || "").split(".").pop().toLowerCase();

      setFileType(ext);
      setFileContent(url);
      setPreviewPassword("");
      setPlacementDirty(false);
      setShowViewer(true);
    } catch (err) {
      console.error("🚨 Preview error:", err);
      setNotice({ type: "error", message: err.message || "Failed to open preview." });
    }
  };

  // -------------------- Attachments preview --------------------
  const handleViewAttachment = async (att) => {
    if (att?.base64 && att?.contentType) {
      window.open(`data:${att.contentType};base64,${att.base64}`, "_blank");
      return;
    }
    if (!att?._id || !file?._id) {
      setNotice({ type: "error", message: "This attachment is not available for preview." });
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/uploads/files/${file._id}/attachments/${att._id}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Unable to open attachment.");
      }
      openBlob(await response.blob(), att.originalName || att.fileName || "attachment");
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Unable to open attachment." });
    }
  };

  // -------------------- Corrected / resubmitted versions --------------------
  const fetchRevisionBlob = async (version) => {
    const response = await fetch(
      `${API_URL}/api/uploads/files/${file._id}/revisions/${version}/download`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Unable to open the corrected version.");
    }
    return response.blob();
  };

  const handleViewResubmission = async (resub) => {
    if (!file?._id || !resub?.version) {
      setNotice({ type: "error", message: "This corrected version is not available." });
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/uploads/files/${file._id}/revisions/${resub.version}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Unable to open the corrected version.");
      }
      openBlob(await response.blob(), resub.originalName || `Corrected version ${resub.version}`);
      setNotice({ type: "success", message: `Corrected version ${resub.version} opened successfully.` });
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Unable to open the corrected version." });
    }
  };

  const handleSignResubmission = async (resub) => {
    if (!file?._id || !resub?.version) {
      setNotice({ type: "error", message: "This corrected version is not available." });
      return;
    }
    try {
      const blob = await fetchRevisionBlob(resub.version);
      if (fileContent?.startsWith("blob:")) URL.revokeObjectURL(fileContent);
      const correctedName = resub.originalName || `Corrected version ${resub.version}`;
      setViewerRevision(resub);
      setCurrentFile({ ...file, fileName: correctedName });
      setFileType(correctedName.split(".").pop()?.toLowerCase() || "");
      setFileContent(URL.createObjectURL(blob));
      setViewerError(null);
      setSignEnabled(false);
      setSavedSignItems([]);
      setShowViewer(true);
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Unable to sign the corrected version." });
    }
  };

  // -------------------- History combine (schema uses comments arrays) --------------------
  const combinedHistory = useMemo(() => buildCombinedHistory(file), [file]);

  const inferStampRank = inferStampRankFromRole(normalizedRole);

  const applyTemplateStamp = async (templateDataUrl, defaultScale = 1.8) => {
    const saved = await saveStamp(templateDataUrl);
    setStampImage(saved);
    setUseStamp(true);
    setStampScale(defaultScale);
    setShowStampUploader(false);
    setTimeout(() => placeSignatureOnStamp(), 120);
  };

  const getPreferredTemplateForRole = () => {
    if (showHqArmyRoleTemplate) {
      return {
        dataUrl: createRoleBoxStampTemplateDataUrl(normalizedRole, inferStampRank || "MAJ"),
        scale: 1.95,
      };
    }
    if (showColCisTemplate) {
      return {
        dataUrl: createRoleBoxStampTemplateDataUrl("COL CIS", "COL"),
        scale: 2.0,
      };
    }
    if (showSO1CommsOrITTemplate) {
      return {
        dataUrl: createRoleBoxStampTemplateDataUrl("SOI COMMS/IT", "LT COL"),
        scale: 1.9,
      };
    }
    if (showSO2CommsTemplate) {
      return {
        dataUrl: createRoleBoxStampTemplateDataUrl("SOII COMMS", "MAJ"),
        scale: 1.9,
      };
    }
    if (showSO2ITTemplate) {
      return {
        dataUrl: createRoleBoxStampTemplateDataUrl("SO II ICT", "MAJ"),
        scale: 1.9,
      };
    }

    // No unit-scoped fallback: a user must never receive another unit's table
    // template. Only the user's own role-box template is ever auto-applied.
    return null;
  };

  const applyQuickStampSetup = async () => {
    setStampError("");
    const preferred = getPreferredTemplateForRole();
    if (!preferred) {
      setStampError("No appointment template is available for your unit — upload your unit's own stamp image instead.");
      return;
    }
    await applyTemplateStamp(preferred.dataUrl, preferred.scale);
    setUseSignature(true);
    setUseStamp(true);
    setTimeout(() => {
      const moved = placeStampNearBottom();
      if (moved) {
        setTimeout(() => placeSignatureOnStamp(), 80);
      } else {
        placeSignatureOnStamp();
      }
    }, 220);
  };

  const saveStamp = async (dataUrl) => {
    if (!authToken) throw new Error("Missing auth token.");
    if (!userFromToken?.userId) throw new Error("Missing userId in token.");
    if (!dataUrl || !dataUrl.startsWith("data:image/")) {
      throw new Error("Invalid stamp image.");
    }

    const payload = JSON.stringify({ stamp: dataUrl });
    const common = {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: payload,
    };

    const res = await fetch(`${API_URL}/api/users/${userFromToken.userId}/stamp`, common);

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error("Session expired. Please log in again.");
      }
      throw new Error(data.message || "Failed to save stamp.");
    }

    return data.stamp?.image || data.stamp || dataUrl;
  };

  const buildSignItemsForSubmit = () => {
    const items = [];
    if (useSignature && sigImage) {
      items.push({
        type: "signature",
        image: sigImage,
        position: { ...sigPos, rotationDeg: sigRotation, pageNumber: pdfPageNumber, cryptoSign: sigPos.cryptoSign },
      });
    }
    if (useStamp && stampImage) {
      items.push({
        type: "stamp",
        image: stampImage,
        position: { ...stampPos, rotationDeg: stampRotation, pageNumber: pdfPageNumber, cryptoSign: stampPos.cryptoSign },
      });
    }
    return items;
  };

  const buildAllSignItemsForSubmit = () => {
    const currentPage = Number(pdfPageNumber || 1);
    const otherPages = (savedSignItems || []).filter(
      (item) => Number(item?.position?.pageNumber || 1) !== currentPage
    );
    return [...otherPages, ...buildSignItemsForSubmit()];
  };

  const rememberCurrentPagePlacement = () => {
    const currentPage = Number(pdfPageNumber || 1);
    const currentItems = buildSignItemsForSubmit();
    setSavedSignItems((previous) => [
      ...(previous || []).filter(
        (item) => Number(item?.position?.pageNumber || 1) !== currentPage
      ),
      ...currentItems,
    ]);
  };

  const changePreviewPage = (nextPage) => {
    const targetPage = Math.min(pdfNumPages, Math.max(1, Number(nextPage) || 1));
    if (targetPage === pdfPageNumber) return;
    rememberCurrentPagePlacement();
    const targetItems = (savedSignItems || []).filter(
      (item) => Number(item?.position?.pageNumber || 1) === targetPage
    );
    setPdfPageNumber(targetPage);
    setUseSignature(targetItems.some((item) => item.type === "signature"));
    setUseStamp(targetItems.some((item) => item.type === "stamp"));
    window.setTimeout(() => {
      const metrics = getPreviewSurfaceMetrics();
      if (!metrics) return;
      const signature = targetItems.find((item) => item.type === "signature");
      const stamp = targetItems.find((item) => item.type === "stamp");
      if (signature?.position) {
        setSigPos(signature.position);
        setSigPosPx({
          x: metrics.originX + Number(signature.position.xPct || 0) * metrics.width,
          y: metrics.originY + Number(signature.position.yPct || 0) * metrics.height,
        });
        setSigScale(Math.max(0.3, Number(signature.position.wPct || 0.2) * metrics.width / 120));
        setSigRotation(Number(signature.position.rotationDeg || 0));
      }
      if (stamp?.position) {
        setStampPos(stamp.position);
        setStampPosPx({
          x: metrics.originX + Number(stamp.position.xPct || 0) * metrics.width,
          y: metrics.originY + Number(stamp.position.yPct || 0) * metrics.height,
        });
        setStampScale(Math.max(0.3, Number(stamp.position.wPct || 0.2) * metrics.width / 120));
        setStampRotation(Number(stamp.position.rotationDeg || 0));
      }
    }, 120);
  };

  // -------------------- Actions (call BaseDashboard handler) --------------------
  const handleApprove = () => {
    if (processing) return;
    if (!forwardingPayload) {
      setNotice({ type: "error", message: "Select the next recipient before approving this document." });
      return;
    }

    const officeAppointment = currentOfficeScope?.appointments?.find((item) => normalizeRouteValue(item.name) === normalizeRouteValue(forwardingPayload.recipient));
    if (restrictToCurrentOffice && (!currentOfficeScope || !officeAppointment || !recipientOptions.some((item) => normalizeRouteValue(item) === normalizeRouteValue(forwardingPayload.recipient)))) {
      setNotice({ type: "error", message: "Refresh your office recipients and select again." });
      return;
    }
    const signItems = buildAllSignItemsForSubmit();
    const shouldSign =
      signEnabled &&
      (fileType === "pdf" || ["jpg", "jpeg", "png"].includes(fileType)) &&
      signItems.length > 0;
    const primary = signItems[0];
    onAction(
      file._id,
      "approve",
      comment,
      attachmentsToSend,
      forwardingPayload.recipient,
      shouldSign ? primary.image : "",
      shouldSign ? primary.position : { xPct: 0, yPct: 0 },
      shouldSign ? primary.type : "signature",
      shouldSign ? signItems : [],
      {
        ...(restrictToCurrentOffice ? {
          routingMode: "CURRENT_OFFICE",
          destinationOfficeId: currentOfficeScope.destinationOfficeId,
          routingAssignmentId: currentOfficeScope.routingAssignmentId,
          destinationAppointmentId: officeAppointment.id,
        } : {}),
        destinationUnit: selectedDestinationScope.unit,
        destinationUnitBranch:
          selectedDestinationScope.unitBranch,
        destinationBranch: selectedDestinationScope.branch,
        destinationSubBranch:
          selectedDestinationScope.subBranch,
      }
    );
  };

  const handleReview = () => {
    if (processing) return;
    if (!comment.trim()) {
      setNotice({ type: "error", message: "Add a comment explaining why this document is being returned to its sender." });
      return;
    }
    const signItems = buildAllSignItemsForSubmit();
    const shouldSign =
      signEnabled &&
      (fileType === "pdf" || ["jpg", "jpeg", "png"].includes(fileType)) &&
      signItems.length > 0;
    const primary = signItems[0];
    onAction(
      file._id,
      "review",
      comment,
      attachmentsToSend,
      "",
      shouldSign ? primary.image : "",
      shouldSign ? primary.position : { xPct: 0, yPct: 0 },
      shouldSign ? primary.type : "signature",
      shouldSign ? signItems : []
    );
  };

  const handleArchive = () => {
    if (processing) return;
    if (!comment.trim()) {
      setNotice({ type: "error", message: "Add an archive comment before continuing." });
      return;
    }
    const signItems = buildAllSignItemsForSubmit();
    const shouldSign =
      signEnabled &&
      (fileType === "pdf" || ["jpg", "jpeg", "png"].includes(fileType)) &&
      signItems.length > 0;
    const primary = signItems[0];
    onAction(
      file._id,
      "archive",
      comment,
      attachmentsToSend,
      "",
      shouldSign ? primary.image : "",
      shouldSign ? primary.position : { xPct: 0, yPct: 0 },
      shouldSign ? primary.type : "signature",
      shouldSign ? signItems : [],
      { archiveGroupIndex: relatedArchiveIndex.trim() }
    );
  };

  const handleDelete = () => {
    if (!processing) setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (processing) return;
    setShowDeleteDialog(false);
    onAction(file._id, "delete");
  };

  const handleResubmit = () => {
    const picker = document.createElement("input");
    picker.type = "file";
    picker.accept = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg";
    picker.onchange = () => {
      const correctedFile = picker.files?.[0];
      if (!correctedFile) return;
      setResubmitFile(correctedFile);
      setResubmitComment("Corrections completed as requested.");
      setShowResubmitDialog(true);
    };
    picker.click();
  };

  const submitCorrectedDocument = async () => {
    if (!resubmitFile || !resubmitComment.trim()) {
      setNotice({ type: "error", message: "Select a corrected file and describe the corrections made." });
      return;
    }

    setResubmitBusy(true);
    try {
      const body = new FormData();
      body.append("file", resubmitFile);
      body.append("comment", resubmitComment.trim());
      const response = await fetch(`${API_URL}/api/uploads/files/${file._id}/resubmit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Resubmission failed.");
      setShowResubmitDialog(false);
      setResubmitFile(null);
      setNotice({ type: "success", message: data.message || "Corrected document resubmitted." });
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Failed to resubmit the corrected document." });
    } finally {
      setResubmitBusy(false);
    }
  };

  const previewBaseWidth = 600;
  const previewRenderWidth = Math.round(previewBaseWidth * previewZoom);
  const receipt = useMemo(() => deriveReceipt(file), [file]);
  const { perspectiveLabel, perspectivePath, currentRecipientPath } = useMemo(
    () => derivePerspective(file, userFromToken),
    [file, userFromToken]
  );
  const statusTone = getStatusTone(file?.status);
  const studioKind = studioKindForName(file?.originalName || file?.fileName);

  return (
    <div className="file-card">
      <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} />
      <FileCardHeader file={file} />

      <DeliveryReceipt receipt={receipt} />

      <section className="file-card__workbench" aria-label="Document workbench">
        <div className="file-card__stage">
          <div className="file-card__stage-heading">
            <div>
              <span className="file-card__eyebrow">Document preview</span>
              <h4>{file?.fileName || "Document"}</h4>
            </div>
            <span className="file-card__stage-type">{fileType ? fileType.toUpperCase() : "FILE"}</span>
          </div>
          <button
            type="button"
            onClick={() => studioKind ? handleOpenInStudio(file) : handleViewFile(file)}
            className="file-card__stage-open"
            disabled={studioOpening}
          >
            {studioKind ? <FilePenLine size={24} aria-hidden="true" /> : <Eye size={24} aria-hidden="true" />}
            <strong>{studioOpening ? "Opening Document Studio…" : studioKind ? "Open in Document Studio" : "Open document preview"}</strong>
            <span>{studioKind ? "View, edit, autosave and prepare this document for workflow action." : "View the original securely in the document viewer."}</span>
          </button>
        </div>

        <aside className="file-card__context">
          <WorkflowTimeline receiptState={receipt.state} isArchived={isArchived} />
          <FileMetaGrid
            file={file}
            isArchived={isArchived}
            perspectiveLabel={perspectiveLabel}
            perspectivePath={perspectivePath}
            currentRecipientPath={currentRecipientPath}
            statusTone={statusTone}
          />

          {Array.isArray(file?.comments) && file.comments.length > 0 && (
            <div className="uploader-comments">
              <h4>
                <MessageSquare size={17} aria-hidden="true" /> Comments
              </h4>
              {file.comments.map((c, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    backgroundColor: "var(--wv-surface)",
                    borderRadius: 6,
                    boxShadow: "var(--wv-shadow-1)",
                    border: "1px solid var(--wv-border)",
                  }}
                >
                  <p style={{ margin: "0 0 4px", fontSize: 14 }}>
                    <strong style={{ color: "var(--wv-secondary)" }}>{c.role || "User"}:</strong>{" "}
                    {c.text || <em style={{ color: "var(--wv-text-muted)" }}>No comment</em>}
                  </p>
                  {c.createdAt && (
                    <p style={{ margin: 0, fontSize: 12, color: "var(--wv-text-muted)" }}>
                      <Clock3 size={13} aria-hidden="true" /> {formatTimestamp(c.createdAt)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </aside>
      </section>

      {/* ✅ Resubmissions */}
      <ResubmissionList
        revisions={file?.revisions}
        isArchived={isArchived}
        onView={handleViewResubmission}
        onSign={handleSignResubmission}
      />

      {/* ✅ Attachments preview from base64 */}
      <AttachmentList attachments={file?.attachments} isArchived={isArchived} onView={handleViewAttachment} />

      {/* Password Prompt Modal */}
      <PasswordPromptModal
        open={showPasswordPopup && !!currentFile}
        file={currentFile}
        value={passwordPrompt}
        error={passwordError}
        busy={isVerifyingPassword}
        onChange={setPasswordPrompt}
        onVerify={verifyPassword}
        onCancel={() => {
          setShowPasswordPopup(false);
          setPasswordPrompt("");
        }}
      />

      {/* File Viewer Modal (PDF/Image after password verification) */}
      {showViewer && fileContent && (
        <div
          className="file-card__modal-overlay"
        >
          <div
            className="file-card__modal-panel file-card__modal-panel--wide"
          >
            <h3>{currentFile?.fileName || "Unknown File"}</h3>

            {fileType === "pdf" ? (
              <PdfErrorBoundary>
                <div
                  className="file-card__preview file-card__preview--interactive"
                  ref={pdfPreviewRef}
                >
                  <Document
                    file={fileContent}
                    options={pdfOptions}
                    loading={<div>Loading PDF...</div>}
                    onLoadSuccess={({ numPages }) => {
                      setPdfNumPages(numPages || 1);
                      setPdfPageNumber((p) => Math.min(Math.max(1, p || 1), numPages || 1));
                    }}
                    onLoadError={(error) => {
                      console.error("PDF load error:", error);
                      setViewerError(`Failed to load PDF: ${error.message || "Unknown error"}`);
                    }}
                  >
                    <Page
                      pageNumber={pdfPageNumber}
                      width={previewRenderWidth}
                      renderTextLayer
                      renderAnnotationLayer
                      loading={<div>Loading page...</div>}
                      onRenderSuccess={() => {
                        // Ensure pct is updated after first render.
                        if (useSignature && sigImage) updateSigFromPx(sigPosPx, pdfPageNumber);
                        if (useStamp && stampImage) updateStampFromPx(stampPosPx, pdfPageNumber);
                      }}
                    />
                  </Document>

                  {signEnabled && snapToGrid && (
                    <div
                      className="file-card__grid"
                      style={{
                        backgroundSize: `${gridSize}px ${gridSize}px`,
                      }}
                    />
                  )}

                  {/* Signature overlay */}
                  {signEnabled && useSignature && sigImage && (
                    <div
                      ref={sigRef}
                      className="file-card__signature"
                      style={{
                        width: `${Math.round(120 * sigScale)}px`,
                        height: `${Math.round(60 * sigScale)}px`,
                        transform: `translate(${sigPosPx.x}px, ${sigPosPx.y}px) rotate(${sigRotation}deg)`,
                        transformOrigin: "top left",
                        zIndex: 12,
                      }}
                      onPointerDown={onSigPointerDown}
                      onPointerMove={onSigPointerMove}
                      onPointerUp={onSigPointerUp}
                      onPointerCancel={onSigPointerUp}
                    >
                      <img src={sigImage} alt="Signature" draggable={false} />
                      <button
                        type="button"
                        className="file-card__signature-resize"
                        onPointerDown={onSigResizePointerDown}
                        onPointerMove={onSigPointerMove}
                        onPointerUp={onSigPointerUp}
                        onPointerCancel={onSigPointerUp}
                        aria-label="Resize signature"
                        title="Drag to resize"
                      />
                      <small>
                        SIG page {pdfPageNumber}/{pdfNumPages} xPct {sigPos.xPct.toFixed(3)} yPct {sigPos.yPct.toFixed(3)}
                      </small>
                    </div>
                  )}

                  {/* Stamp overlay */}
                  {signEnabled && useStamp && stampImage && (
                    <div
                      ref={stampRef}
                      className="file-card__signature"
                      style={{
                        width: `${Math.round(120 * stampScale)}px`,
                        height: `${Math.round(60 * stampScale)}px`,
                        transform: `translate(${stampPosPx.x}px, ${stampPosPx.y}px) rotate(${stampRotation}deg)`,
                        transformOrigin: "top left",
                        borderColor: "#16a34a",
                        zIndex: 9,
                      }}
                      onPointerDown={onStampPointerDown}
                      onPointerMove={onStampPointerMove}
                      onPointerUp={onSigPointerUp}
                      onPointerCancel={onSigPointerUp}
                    >
                      <img src={stampImage} alt="Stamp" draggable={false} />
                      <button
                        type="button"
                        className="file-card__signature-resize"
                        onPointerDown={onStampResizePointerDown}
                        onPointerMove={onStampPointerMove}
                        onPointerUp={onSigPointerUp}
                        onPointerCancel={onSigPointerUp}
                        aria-label="Resize stamp"
                        title="Drag to resize"
                      />
                      <small>
                        STAMP page {pdfPageNumber}/{pdfNumPages} xPct {stampPos.xPct.toFixed(3)} yPct {stampPos.yPct.toFixed(3)}
                      </small>
                    </div>
                  )}
                </div>

                {/* PDF page navigation */}
                <div className="pdf-nav">
                  <button
                    onClick={() => changePreviewPage(pdfPageNumber - 1)}
                    disabled={pdfPageNumber <= 1}
                  >
                    Prev Page
                  </button>
                  <span>
                    Page {pdfPageNumber} / {pdfNumPages}
                  </span>
                  <button
                    onClick={() => changePreviewPage(pdfPageNumber + 1)}
                    disabled={pdfPageNumber >= pdfNumPages}
                  >
                    Next Page
                  </button>
                </div>
              </PdfErrorBoundary>
            ) : ["jpg", "jpeg", "png"].includes(fileType) ? (
              <div className="file-card__preview file-card__preview--interactive" ref={pdfPreviewRef}>
                <img
                  src={fileContent}
                  alt="File"
                  className="file-card__image"
                  style={{ width: `${Math.round(100 * previewZoom)}%` }}
                />
                {signEnabled && snapToGrid && (
                  <div
                    className="file-card__grid"
                    style={{
                      backgroundSize: `${gridSize}px ${gridSize}px`,
                    }}
                  />
                )}
                {signEnabled && useSignature && sigImage && (
                  <div
                    ref={sigRef}
                    className="file-card__signature"
                    style={{
                      left: sigPosPx.x,
                      top: sigPosPx.y,
                      width: `${Math.round(120 * sigScale)}px`,
                      height: `${Math.round(60 * sigScale)}px`,
                      transform: `rotate(${sigRotation}deg)`,
                      transformOrigin: "top left",
                      zIndex: 12,
                    }}
                    onPointerDown={onSigPointerDown}
                    onPointerMove={onSigPointerMove}
                    onPointerUp={onSigPointerUp}
                    onPointerCancel={onSigPointerUp}
                  >
                    <img src={sigImage} alt="Signature" draggable={false} />
                    <button
                      type="button"
                      className="file-card__signature-resize"
                      onPointerDown={onSigResizePointerDown}
                      onPointerMove={onSigPointerMove}
                      onPointerUp={onSigPointerUp}
                      onPointerCancel={onSigPointerUp}
                      aria-label="Resize signature"
                      title="Drag to resize"
                    />
                    <small>
                      SIG xPct {sigPos.xPct.toFixed(3)} yPct {sigPos.yPct.toFixed(3)}
                    </small>
                  </div>
                )}
                {signEnabled && useStamp && stampImage && (
                  <div
                    ref={stampRef}
                    className="file-card__signature"
                    style={{
                      left: stampPosPx.x,
                      top: stampPosPx.y,
                      width: `${Math.round(120 * stampScale)}px`,
                      height: `${Math.round(60 * stampScale)}px`,
                      transform: `rotate(${stampRotation}deg)`,
                      transformOrigin: "top left",
                      borderColor: "#16a34a",
                      zIndex: 9,
                    }}
                    onPointerDown={onStampPointerDown}
                    onPointerMove={onStampPointerMove}
                    onPointerUp={onSigPointerUp}
                    onPointerCancel={onSigPointerUp}
                  >
                    <img src={stampImage} alt="Stamp" draggable={false} />
                    <button
                      type="button"
                      className="file-card__signature-resize"
                      onPointerDown={onStampResizePointerDown}
                      onPointerMove={onStampPointerMove}
                      onPointerUp={onSigPointerUp}
                      onPointerCancel={onSigPointerUp}
                      aria-label="Resize stamp"
                      title="Drag to resize"
                    />
                    <small>
                      STAMP xPct {stampPos.xPct.toFixed(3)} yPct {stampPos.yPct.toFixed(3)}
                    </small>
                  </div>
                )}
              </div>
            ) : (
              <p>Unsupported preview type.</p>
            )}

            {viewerError && <p style={{ color: "red" }}>{viewerError}</p>}

            {/* Signature/stamp controls */}
            {(fileType === "pdf" || ["jpg", "jpeg", "png"].includes(fileType)) && !isSummaryView && (
              <div className="signature-section" style={{ marginTop: 12, flexDirection: "column", alignItems: "stretch" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={signEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setSignEnabled(enabled);
                      if (enabled && !sigImage) setShowSigPad(true);
                      if (enabled && !stampImage) setShowStampUploader(true);
                    }}
                  />
                  Enable signing
                </label>

                {signEnabled && (
                  <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Use:</span>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={useSignature && !!sigImage}
                        onChange={(e) => setUseSignature(e.target.checked)}
                        disabled={!sigImage}
                      />
                      Signature
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={useStamp && !!stampImage}
                        onChange={(e) => setUseStamp(e.target.checked)}
                        disabled={!stampImage}
                      />
                      Stamp
                    </label>
                  </div>
                )}

                {(sigLoading || stampLoading) && <span className="loading">Loading officer assets...</span>}
                {sigError && <span className="error">Signature: {sigError}</span>}
                {stampError && <span className="error">Stamp: {stampError}</span>}

                {signEnabled && (
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: sigImage ? "#166534" : "#9a3412" }}>
                        {sigImage ? "Signature loaded" : "No saved signature"}
                      </span>
                      {sigImage && (
                        <img
                          src={sigImage}
                          alt="Loaded signature"
                          style={{ height: 28, maxWidth: 120, objectFit: "contain", border: "1px solid #cbd5e1", background: "#fff" }}
                        />
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowSigPad((v) => !v)}
                      >
                        {showSigPad ? "Hide Signature Pad" : "Replace Signature"}
                      </button>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: stampImage ? "#166534" : "#9a3412" }}>
                        {stampImage ? "Stamp loaded" : "No saved stamp"}
                      </span>
                      {stampImage && (
                        <img
                          src={stampImage}
                          alt="Loaded stamp"
                          style={{ height: 28, maxWidth: 120, objectFit: "contain", border: "1px solid #cbd5e1", background: "#fff" }}
                        />
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowStampUploader((v) => !v)}
                      >
                        {showStampUploader ? "Hide Stamp Upload" : "Replace Stamp"}
                      </button>
                    </div>
                  </div>
                )}

                {signEnabled && (
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!sigImage}
                      onClick={async () => {
                        try {
                          await applyQuickStampSetup();
                        } catch (err) {
                          setStampError(err.message || "Failed to run quick setup.");
                        }
                      }}
                      title={sigImage ? "Auto-apply stamp template and place signature" : "Load or save your signature first"}
                    >
                      Quick Setup (Auto Place)
                    </button>
                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      Zoom
                      <input
                        type="range"
                        min="0.6"
                        max="2.2"
                        step="0.1"
                        value={previewZoom}
                        onChange={(e) => setPreviewZoom(Number(e.target.value))}
                      />
                      <span style={{ minWidth: 40 }}>{Math.round(previewZoom * 100)}%</span>
                    </label>

                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      Snap Grid
                      <input
                        type="checkbox"
                        checked={snapToGrid}
                        onChange={(e) => setSnapToGrid(e.target.checked)}
                      />
                    </label>

                    {snapToGrid && (
                      <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        Grid
                        <input
                          type="range"
                          min="8"
                          max="48"
                          step="4"
                          value={gridSize}
                          onChange={(e) => setGridSize(Number(e.target.value))}
                        />
                        <span style={{ minWidth: 34 }}>{gridSize}px</span>
                      </label>
                    )}

                    {useSignature && sigImage && (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1d4ed8" }}>Signature</span>
                        {useStamp && stampImage && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => placeSignatureOnStamp()}
                          >
                            Place Signature On Stamp
                          </button>
                        )}
                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          Scale
                          <input
                            type="range"
                            min="0.3"
                            max="2"
                            step="0.05"
                            value={sigScale}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setSigScale(v);
                              setTimeout(() => updateSigFromPx(sigPosPx), 0);
                            }}
                          />
                        </label>

                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          Rotate
                          <input
                            type="number"
                            value={sigRotation}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setSigRotation(v);
                              setTimeout(() => updateSigFromPx(sigPosPx), 0);
                            }}
                            style={{ width: 90 }}
                          />
                        </label>

                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          Crypto Sign (PDF)
                          <input
                            type="checkbox"
                            checked={!!sigPos.cryptoSign}
                            onChange={(e) => setSigPos((p) => ({ ...p, cryptoSign: e.target.checked }))}
                            disabled={fileType !== "pdf"}
                          />
                        </label>
                      </>
                    )}

                    {useStamp && stampImage && (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>Stamp</span>
                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          Scale
                          <input
                            type="range"
                            min="0.3"
                            max="2"
                            step="0.05"
                            value={stampScale}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setStampScale(v);
                              setTimeout(() => updateStampFromPx(stampPosPx), 0);
                            }}
                          />
                        </label>

                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          Rotate
                          <input
                            type="number"
                            value={stampRotation}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setStampRotation(v);
                              setTimeout(() => updateStampFromPx(stampPosPx), 0);
                            }}
                            style={{ width: 90 }}
                          />
                        </label>
                      </>
                    )}
                  </div>
                )}

                {/* If user has no saved signature (or wants replacement), allow drawing + saving */}
                {signEnabled && !sigLoading && (!sigImage || showSigPad) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>
                      {!sigImage ? "No saved signature. Draw one and save:" : "Draw replacement signature and save:"}
                    </div>
                    <SignatureCanvas
                      ref={sigPadRef}
                      penColor={SIG_PEN_COLOR}
                      canvasProps={{ width: 360, height: 140, className: "sig-canvas" }}
                    />
                    <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => sigPadRef.current?.clear()}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={async () => {
                          try {
                            setSigError("");
                            if (!authToken) throw new Error("Missing auth token.");
                            if (!userFromToken?.userId) throw new Error("Missing userId in token.");

                            const pad = sigPadRef.current;
                            if (!pad) throw new Error("Signature pad is not ready.");
                            if (typeof pad.isEmpty === "function" && pad.isEmpty()) {
                              throw new Error("Please draw your signature first.");
                            }

                            let canvas =
                              typeof pad.getCanvas === "function" ? pad.getCanvas() : null;

                            if (!canvas && typeof pad.getTrimmedCanvas === "function") {
                              try {
                                canvas = pad.getTrimmedCanvas();
                              } catch {
                                // Fallback already handled by getCanvas path.
                              }
                            }

                            if (!canvas || typeof canvas.toDataURL !== "function") {
                              throw new Error("Unable to read signature from canvas.");
                            }

                            const dataUrl = canvas.toDataURL("image/png");
                            if (!dataUrl || !dataUrl.startsWith("data:image/")) {
                              throw new Error("Invalid signature image generated.");
                            }

                            const blueDataUrl = await recolorSignatureToBlue(dataUrl);
                            const payload = JSON.stringify({ signature: blueDataUrl });
                            const common = {
                              method: "PUT",
                              headers: {
                                Authorization: `Bearer ${authToken}`,
                                "Content-Type": "application/json",
                              },
                              body: payload,
                            };

                            const res = await fetch(`${API_URL}/api/users/${userFromToken.userId}/signature`, common);

                            const data = await res.json().catch(() => ({}));
                            if (!res.ok) {
                              if (res.status === 401 || res.status === 403) {
                                throw new Error("Session expired. Please log in again.");
                              }
                              throw new Error(data.message || "Failed to save signature.");
                            }

                            setSigError("");
                            setSigImage(data.signature?.image || data.signature || blueDataUrl);
                            setUseSignature(true);
                            setShowSigPad(false);
                          } catch (e) {
                            setSigError(e.message || "Failed to save signature.");
                          }
                        }}
                      >
                        Save Signature
                      </button>
                    </div>
                  </div>
                )}

                {/* If user has no saved stamp (or wants replacement), allow upload + save */}
                {signEnabled && !stampLoading && (!stampImage || showStampUploader) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>
                      {!stampImage ? "No saved stamp. Upload one and save:" : "Upload replacement stamp and save:"}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={async (e) => {
                        const rawFile = e.target.files?.[0];
                        if (!rawFile) return;

                        try {
                          setStampError("");
                          const dataUrl = await fileToDataUrl(rawFile);
                          if (!dataUrl || !dataUrl.startsWith("data:image/")) {
                            throw new Error("Invalid stamp image selected.");
                          }

                          const { width, height } = await getImageSize(dataUrl, "stamp");
                          if (width <= 5 || height <= 5) {
                            throw new Error("Selected stamp image is too small/blank.");
                          }

                          const saved = await saveStamp(dataUrl);
                          setStampImage(saved);
                          setUseStamp(true);
                          setShowStampUploader(false);
                        } catch (err) {
                          setStampError(err.message || "Failed to save stamp.");
                        } finally {
                          e.target.value = "";
                        }
                      }}
                    />
                    {showHqArmyRoleTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ marginTop: 8, marginLeft: 8 }}
                        onClick={async () => {
                          try {
                            setStampError("");
                            const templateDataUrl = createRoleBoxStampTemplateDataUrl(
                              normalizedRole,
                              inferStampRank || "MAJ"
                            );
                            await applyTemplateStamp(templateDataUrl, 1.95);
                          } catch (err) {
                            setStampError(err.message || "Failed to create appointment stamp.");
                          }
                        }}
                      >
                        Use My Appointment Template
                      </button>
                    )}
                    {showSO2CommsTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ marginTop: 8, marginLeft: 8 }}
                        onClick={async () => {
                          try {
                            setStampError("");
                            const templateDataUrl = createRoleBoxStampTemplateDataUrl("SOII COMMS", "MAJ");
                            await applyTemplateStamp(templateDataUrl, 1.9);
                          } catch (err) {
                            setStampError(err.message || "Failed to create SOII COMMS stamp.");
                          }
                        }}
                      >
                        Use SOII COMMS Template
                      </button>
                    )}
                    {showSO2ITTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ marginTop: 8, marginLeft: 8 }}
                        onClick={async () => {
                          try {
                            setStampError("");
                            const templateDataUrl = createRoleBoxStampTemplateDataUrl("SO II ICT", "MAJ");
                            await applyTemplateStamp(templateDataUrl, 1.9);
                          } catch (err) {
                            setStampError(err.message || "Failed to create SO II ICT stamp.");
                          }
                        }}
                      >
                        Use SO II ICT Template
                      </button>
                    )}
                    {showSO1CommsOrITTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ marginTop: 8, marginLeft: 8 }}
                        onClick={async () => {
                          try {
                            setStampError("");
                            const templateDataUrl = createRoleBoxStampTemplateDataUrl("SOI COMMS/IT", "LT COL");
                            await applyTemplateStamp(templateDataUrl, 1.9);
                          } catch (err) {
                            setStampError(err.message || "Failed to create SOI COMMS/IT stamp.");
                          }
                        }}
                      >
                        Use SOI COMMS/IT Template
                      </button>
                    )}
                    {showColCisTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        style={{ marginTop: 8, marginLeft: 8 }}
                        onClick={async () => {
                          try {
                            setStampError("");
                            const templateDataUrl = createRoleBoxStampTemplateDataUrl("COL CIS", "COL");
                            await applyTemplateStamp(templateDataUrl, 2.0);
                          } catch (err) {
                            setStampError(err.message || "Failed to create COL CIS stamp.");
                          }
                        }}
                      >
                        Use COL CIS Template
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="file-card__viewer-actions">
              <button type="button" className="file-card__btn file-card__btn-cancel" onClick={dismissViewer} disabled={savingPlacement}>
                Cancel
              </button>
              <button
                type="button"
                className="file-card__btn file-card__btn-approve"
                onClick={applyAndCloseViewer}
                disabled={savingPlacement || !signEnabled || !placementDirty}
              >
                {savingPlacement ? "Applying…" : "Apply & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Combined history + reviewHistory */}
      <FileHistoryTimeline entries={combinedHistory} onOpenAttachment={handleViewAttachment} />

      {/* Actions visible only if not archived/final/summary */}
      {!isSummaryView && !isFinalApproved && (
        <>
          {!isArchived && (
            <>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comment (required for Review/Archive)"
                className="form-control"
                rows={3}
              />

              <label style={{ marginTop: 10 }}>Add Attachments (Optional):</label>
              <input
                type="file"
                multiple
                onChange={(e) => setAttachmentsToSend(Array.from(e.target.files))}
                className="file-card__input"
              />

              {/* Recipient selection (required for approve/forward flow) */}
              <div id={`forward-controls-${file?._id || "file"}`} style={{ scrollMarginTop: 72 }}>
                {restrictToCurrentOffice || (Array.isArray(recipientOptions) && recipientOptions.length > 0) ? (
                  <div className="file-card__select">
                    <label>Recipient:</label>
                    <input
                      type="text"
                      value={quickRecipientSearch}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setQuickRecipientSearch(nextValue);
                        setShowQuickRecipientSuggestions(true);
                        const matchedEntry = findQuickRecipientEntry(nextValue);
                        if (matchedEntry) applyRecipientEntry(matchedEntry);
                        else setSelectedRecipient("");
                      }}
                      onFocus={() => setShowQuickRecipientSuggestions(true)}
                      onBlur={() => window.setTimeout(() => setShowQuickRecipientSuggestions(false), 150)}
                      placeholder="Start typing recipient name"
                      list="quick-recipient-options"
                      className="file-card__select-input"
                      style={{ marginBottom: 8 }}
                    />
                    <datalist id="quick-recipient-options">
                      {quickRecipientSuggestions.map((entry) => (
                        <option key={entry.label} value={entry.recipient}>
                          {entry.label}
                        </option>
                      ))}
                    </datalist>
                    {renderQuickRecipientSuggestions()}
                  </div>
                ) : (
                  <>
                    <div className="file-card__select" style={{ marginTop: 10 }}>
                      <label>Quick Recipient Search:</label>
                      <input
                        type="text"
                        value={quickRecipientSearch}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setQuickRecipientSearch(nextValue);
                          setShowQuickRecipientSuggestions(true);
                          const matchedEntry = findQuickRecipientEntry(nextValue);
                          if (matchedEntry) applyRecipientEntry(matchedEntry);
                          else setSelectedRecipient("");
                        }}
                        onFocus={() => setShowQuickRecipientSuggestions(true)}
                        onBlur={() => window.setTimeout(() => setShowQuickRecipientSuggestions(false), 150)}
                        placeholder="Type recipient name to auto-fill path"
                        list="quick-recipient-options"
                        className="file-card__select-input"
                        style={{ marginBottom: 8 }}
                      />
                      <datalist id="quick-recipient-options">
                        {quickRecipientSuggestions.map((entry) => (
                          <option key={entry.label} value={entry.recipient}>
                            {entry.label}
                          </option>
                        ))}
                      </datalist>
                      {renderQuickRecipientSuggestions()}
                    </div>

                    {false && (<>
                    <div className="file-card__select" style={{ marginTop: 10 }}>
                      <label>Brigade / Formation:</label>
                      <input
                        type="text"
                        value={unitFilter}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setUnitFilter(nextValue);
                          const matchedUnit = findExactOption(structureOptions, nextValue);
                          if (matchedUnit) {
                            setSelectedUnit(matchedUnit);
                            setSelectedBranch("");
                            setSelectedSubBranch("");
                            setSelectedSection("");
                            setSelectedRecipient("");
                            setBranchFilter("");
                            setSubBranchFilter("");
                            setSectionFilter("");
                            setRecipientFilter("");
                          }
                        }}
                        placeholder="Start typing brigade or formation"
                        list="forward-hierarchy-options"
                        className="file-card__select-input"
                        style={{ marginBottom: 8 }}
                      />
                      <datalist id="forward-hierarchy-options">
                        {filteredHierarchyOptions.map((unit) => (
                          <option key={unit} value={unit} />
                        ))}
                      </datalist>
                      <select
                        value={selectedUnit}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setSelectedUnit(nextValue);
                          setUnitFilter(nextValue);
                          setSelectedBranch("");
                          setSelectedSubBranch("");
                          setSelectedSection("");
                          setSelectedRecipient("");
                          setBranchFilter("");
                          setSubBranchFilter("");
                          setSectionFilter("");
                          setRecipientFilter("");
                        }}
                        className="file-card__select-input"
                      >
                        <option value="">Select Brigade / Formation</option>
                        {filteredHierarchyOptions.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedUnit && (
                      <div className="file-card__select">
                        <label>Unit / Branch:</label>
                        <input
                          type="text"
                          value={branchFilter}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setBranchFilter(nextValue);
                            const matchedBranch = findExactOption(branchOptions, nextValue);
                            if (matchedBranch) {
                              setSelectedBranch(matchedBranch);
                              setSelectedSubBranch("");
                              setSelectedSection("");
                              setSelectedRecipient("");
                              setSubBranchFilter("");
                              setSectionFilter("");
                              setRecipientFilter("");
                            }
                          }}
                          placeholder="Start typing unit or branch"
                          list="forward-branch-options"
                          className="file-card__select-input"
                          style={{ marginBottom: 8 }}
                        />
                        <datalist id="forward-branch-options">
                          {filteredBranchOptions.map((branch) => (
                            <option key={branch} value={branch} />
                          ))}
                        </datalist>
                        <select
                          value={selectedBranch}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSelectedBranch(nextValue);
                            setBranchFilter(nextValue);
                            setSelectedSubBranch("");
                            setSelectedSection("");
                            setSelectedRecipient("");
                            setSubBranchFilter("");
                            setSectionFilter("");
                            setRecipientFilter("");
                          }}
                          className="file-card__select-input"
                        >
                          <option value="">Select Unit / Branch</option>
                          {filteredBranchOptions.map((branch) => (
                            <option key={branch} value={branch}>
                              {branch}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedBranch && subBranchOptions.length > 0 && (
                      <div className="file-card__select">
                        <label>Sub-Unit / Section:</label>
                        <input
                          type="text"
                          value={subBranchFilter}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSubBranchFilter(nextValue);
                            const matchedSubBranch = findExactOption(subBranchOptions, nextValue);
                            if (matchedSubBranch) {
                              setSelectedSubBranch(matchedSubBranch);
                              setSelectedSection("");
                              setSelectedRecipient("");
                              setSectionFilter("");
                              setRecipientFilter("");
                            }
                          }}
                          placeholder="Start typing sub-unit or section"
                          list="forward-sub-branch-options"
                          className="file-card__select-input"
                          style={{ marginBottom: 8 }}
                        />
                        <datalist id="forward-sub-branch-options">
                          {filteredSubBranchOptions.map((sub) => (
                            <option key={sub} value={sub} />
                          ))}
                        </datalist>
                        <select
                          value={selectedSubBranch}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSelectedSubBranch(nextValue);
                            setSubBranchFilter(nextValue);
                            setSelectedSection("");
                            setSelectedRecipient("");
                            setSectionFilter("");
                            setRecipientFilter("");
                          }}
                          className="file-card__select-input"
                        >
                          <option value="">Select Sub-Unit / Section</option>
                          {filteredSubBranchOptions.map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedSubBranch && sectionOptions.length > 0 && (
                      <div className="file-card__select">
                        <label>Section / Sub-Branch:</label>
                        <input
                          type="text"
                          value={sectionFilter}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSectionFilter(nextValue);
                            const matchedSection = findExactOption(sectionOptions, nextValue);
                            if (matchedSection) {
                              setSelectedSection(matchedSection);
                              setSelectedRecipient("");
                              setRecipientFilter("");
                            }
                          }}
                          placeholder="Start typing section or sub-branch"
                          list="forward-section-options"
                          className="file-card__select-input"
                          style={{ marginBottom: 8 }}
                        />
                        <datalist id="forward-section-options">
                          {filterOptions(sectionOptions, sectionFilter).map((section) => (
                            <option key={section} value={section} />
                          ))}
                        </datalist>
                        <select
                          value={selectedSection}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSelectedSection(nextValue);
                            setSectionFilter(nextValue);
                            setSelectedRecipient("");
                            setRecipientFilter("");
                          }}
                          className="file-card__select-input"
                        >
                          <option value="">Select Section / Sub-Branch</option>
                          {filterOptions(sectionOptions, sectionFilter).map((section) => (
                            <option key={section} value={section}>
                              {section}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedBranch &&
                      (subBranchOptions.length === 0 ||
                        (selectedSubBranch && sectionOptions.length === 0) ||
                        (selectedSubBranch && selectedSection)) && (
                      <div className="file-card__select">
                        <label>Recipient:</label>
                        <input
                          type="text"
                          value={recipientFilter}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setRecipientFilter(nextValue);
                            const matchedRecipient = findExactOption(getRecipients(), nextValue);
                            if (matchedRecipient) {
                              setSelectedRecipient(matchedRecipient);
                            }
                          }}
                          placeholder="Start typing recipient"
                          list="forward-recipient-options"
                          className="file-card__select-input"
                          style={{ marginBottom: 8 }}
                        />
                        <datalist id="forward-recipient-options">
                          {filteredRecipientOptions.map((person) => (
                            <option key={person} value={person} />
                          ))}
                        </datalist>
                        <select
                          value={selectedRecipient}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setSelectedRecipient(nextValue);
                            setRecipientFilter(nextValue);
                          }}
                          className="file-card__select-input"
                        >
                          <option value="">Select Recipient</option>
                          {filteredRecipientOptions.map((person) => (
                            <option key={person} value={person}>
                              {person}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    </>)}
                  </>
                )}
              </div>

              {selectedRecipientDetails && (
                <section className="file-card__recipient-summary" aria-live="polite">
                  <div className="file-card__recipient-icon" aria-hidden="true">
                    <UserRound size={22} />
                  </div>
                  <div className="file-card__recipient-content">
                    <div className="file-card__recipient-heading">
                      <div>
                        <span className="file-card__recipient-eyebrow">Selected recipient</span>
                        <strong>{selectedRecipientDetails.recipient}</strong>
                      </div>
                      <span className="file-card__recipient-status">
                        <CheckCircle2 size={15} aria-hidden="true" /> Confirmed
                      </span>
                    </div>

                    {[selectedRecipientDetails.section, selectedRecipientDetails.subBranch,
                      selectedRecipientDetails.branch, selectedRecipientDetails.formation]
                      .filter(Boolean).length > 0 && (
                      <div className="file-card__recipient-route">
                        <Building2 size={15} aria-hidden="true" />
                        <span>
                          {[selectedRecipientDetails.section, selectedRecipientDetails.subBranch,
                            selectedRecipientDetails.branch, selectedRecipientDetails.formation]
                            .filter(Boolean)
                            .join(" • ")}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {role !== "CLERK" && file?.status !== "Resubmission Required" && (
                <div className="file-card__archive-group">
                  <label htmlFor={`archive-group-${file?._id}`}>Related archive index <span>Optional</span></label>
                  <input
                    id={`archive-group-${file?._id}`}
                    type="text"
                    value={relatedArchiveIndex}
                    onChange={(event) => setRelatedArchiveIndex(event.target.value.toUpperCase())}
                    placeholder="Example: ARC-2026-000123"
                    className="file-card__select-input"
                  />
                  <small>Leave blank for a new archive series. Enter an existing base index to group related documents.</small>
                </div>
              )}

              {file?.status === "Resubmission Required" && (
                <div className="file-card__actions" style={{ marginTop: "1rem" }}>
                  <button type="button" onClick={handleResubmit} className="file-card__btn file-card__btn-approve" disabled={processing}>
                    <Send size={17} aria-hidden="true" /> Submit Corrected Document
                  </button>
                </div>
              )}

                {/* Buttons - hide from CLERK if you want */}
              {role !== "CLERK" && file?.status !== "Resubmission Required" && (
                <PrimaryActions
                  processing={processing}
                  onApprove={handleApprove}
                  onReview={handleReview}
                  onArchive={handleArchive}
                  onDelete={handleDelete}
                />
              )}
            </>
          )}
        </>
      )}

      <ModalDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        labelledBy="delete-dialog-title"
        role="alertdialog"
        icon={<Trash2 size={22} />}
        iconTone="danger"
        title="Delete this document?"
        actions={
          <>
            <button type="button" onClick={() => setShowDeleteDialog(false)} disabled={processing}>Cancel</button>
            <button type="button" onClick={confirmDelete} className="file-card__btn-delete" disabled={processing}>Delete document</button>
          </>
        }
      >
        <p>This permanently removes the document from the system. This action cannot be undone.</p>
      </ModalDialog>

      <ModalDialog
        open={showResubmitDialog}
        onClose={() => setShowResubmitDialog(false)}
        labelledBy="resubmit-dialog-title"
        icon={<Send size={22} />}
        title="Confirm corrected document"
        actions={
          <>
            <button type="button" onClick={() => setShowResubmitDialog(false)} disabled={resubmitBusy}>Cancel</button>
            <button type="button" onClick={submitCorrectedDocument} className="file-card__btn-approve" disabled={resubmitBusy}>
              {resubmitBusy ? "Submitting…" : "Confirm resubmission"}
            </button>
          </>
        }
      >
        <p className="file-card__selected-file">{resubmitFile?.name}</p>
        <label htmlFor={`resubmit-comment-${file?._id}`}>Summary of corrections</label>
        <textarea
          id={`resubmit-comment-${file?._id}`}
          value={resubmitComment}
          onChange={(event) => setResubmitComment(event.target.value)}
          rows={4}
          placeholder="Briefly describe the corrections made"
        />
      </ModalDialog>
    </div>
  );
};

export default FileCard;
